import { z } from 'zod'
import { imageProcessingModeSchema, imagesSchema } from './images'
import { crosssellTypesSchema } from '../crosssellTypes'
import { productPurchaseModeSchema } from '../productPurchaseMode'
import { commerceAttributesSchema } from './commerceAttributes'
import { ebayAccountKeySchema } from '../ebayAccounts'

export const optionSchema = z.object({
  id: z.number(),
  name: z.string(),
  filter: z.array(z.number()).nullable(),
  images: imagesSchema.optional(),
  uncultivable: z.boolean().optional(), 
  order: z.number().default(0),
})
export type Option = z.infer<typeof optionSchema>


export const attributeSchema = z.object({
  id: z.number(),
  name: z.string(),
  options: z.array(optionSchema),
  type: z.string().default('other'),
  order: z.number().default(0),
})
export type Attribute = z.infer<typeof attributeSchema>

export const idealoPriceEntrySchema = z.object({
  preis: z.number(),               // z. B. 16.05
  haendler: z.string(),            // z. B. Shop-Domain
  versand: z.number(),            // z. B. 21.05
  datum: z.string(),              // ISO-Datum
})

export const combinationSchema = z.object({
  key: z.string(),
  attributes: z.array(z.string()),

  price: z.number(),
  ek: z.number().optional(),

  ean: z.string().optional(),
  sku: z.string().optional(),
  mpn: z.string().optional(),
  seasonCode: z.string().optional(),
  originCountry: z.string().optional(),
  commerceAttributes: commerceAttributesSchema.optional(),
  asin: z.string().optional(),
  idealo: z.string().optional(),     // URL zur Idealo-Seite dieser Kombination

  idealo_bestpreis: z.number().optional(), // z. B. "16,05 €"
  idealo_preise: z.array(idealoPriceEntrySchema).optional(), // Verlauf

  deliverytime: z.number(),
  options: z.array(z.number()).optional(),
  filter: z.array(z.number()),
  images: imagesSchema.optional(),

  weight: z.number().optional(),
  vpe: z.number().optional(),

  cultivable: z.boolean(),
})
export type Combination = z.infer<typeof combinationSchema>

export const crossSellProductOptionsSchema = z.object({
  filter: z.array(z.number()).optional(),
  preselect: z.number().optional(),
})
export type CrossSellProductOptions = z.infer<typeof crossSellProductOptionsSchema>

export const crossSellProductSchema = z.object({
  id: z.number(),
  options: crossSellProductOptionsSchema.default({}),
})
export type CrossSellProduct = z.infer<typeof crossSellProductSchema>

export const crossSellSchema = z.object({
  art: crosssellTypesSchema,
  titel: z.string().optional(),
  hashtags: z.array(z.string()).optional(),
  excludedProductIds: z.array(z.number()).optional(),
  products: z.array(crossSellProductSchema).default([]),
})
export type CrossSell = z.infer<typeof crossSellSchema>

export const productImportEntrySchema = z.object({
  ean: z.string().optional(),
  original: z.record(z.any()),
  mapped: z.record(z.any()).optional(),
})
export type ProductImportEntry = z.infer<typeof productImportEntrySchema>

export const productLastImportSchema = z.object({
  importId: z.number().optional(),
  fileName: z.string().optional(),
  notes: z.string().optional(),
  importedAt: z.date().optional(),
  entries: z.array(productImportEntrySchema).default([]),
})
export type ProductLastImport = z.infer<typeof productLastImportSchema>

export const imageAltTagSchema = z.object({
  image: z.string(),
  altTag: z.string(),
})
export type ImageAltTag = z.infer<typeof imageAltTagSchema>

export const imageProcessingModeEntrySchema = z.object({
  image: z.string(),
  processingMode: imageProcessingModeSchema,
})
export type ImageProcessingModeEntry = z.infer<typeof imageProcessingModeEntrySchema>

export const productMarketplaceAccountsSchema = z.object({
  ebay: z.array(ebayAccountKeySchema).optional(),
})
export type ProductMarketplaceAccounts = z.infer<typeof productMarketplaceAccountsSchema>


export const productSchema = z.object({
  id: z.number(),
  brand: z.number(),
  manufacturer: z.number(),
  categorie: z.number(),
  allCategories: z.array(z.number()),
  ordered: z.number(),
  priority: z.number(),
  shippingType: z.number(),

  customsTariffNumber: z.string().optional(),
  commerceAttributes: commerceAttributesSchema.optional(),

  images: imagesSchema.optional(),
  optionImageAltTags: z.array(imageAltTagSchema).optional(),
  combinationImageAltTags: z.array(imageAltTagSchema).optional(),
  optionImageProcessingModes: z.array(imageProcessingModeEntrySchema).optional(),
  combinationImageProcessingModes: z.array(imageProcessingModeEntrySchema).optional(),
  optionImageOrder: z.array(z.string()).optional(),
  combinationImageOrder: z.array(z.string()).optional(),

  attributes: z.array(attributeSchema),
  combinations: z.array(combinationSchema),

  name: z.string(),
  metaTitle: z.string(),
  metaDescription: z.string().optional(),
  alternativeTitle: z.string().optional(),
  description: z.string().optional(),
  descriptionDetails: z.string().optional(),
  descriptionShort: z.string().optional(),
  path: z.string().optional(),
  hook: z.string().optional(),
  productType: z.string().optional(),
  listingGroupKey: z.string().optional(),
  listingVariantMode: z.enum(['attribute', 'combination']).optional(),
  listingVariantAttributeId: z.number().optional(),
  crossSellSerie: z.string().optional(),
  crossSellManufacturer: z.string().optional(),
  crossSellPart: z.string().optional(),
  crossSellColor: z.string().optional(),
  crossSellColorAttributeId: z.number().optional(),
  technicalData: z.array(z.string()).optional(),
  searchKeywordsGenerated: z.array(z.string()).optional(),
  searchKeywordsManual: z.array(z.string()).optional(),
  lastImport: productLastImportSchema.optional(),

  note: z.string().optional(),
  noteStock: z.string().optional(),

  online: z.boolean(),
  onlyStock: z.boolean(),
  purchaseMode: productPurchaseModeSchema.optional(),
  inquireOnly: z.boolean().optional(),
  danger: z.boolean().optional(),
  shippingDanger: z.boolean().optional(),

  shops: z.array(z.number()),
  shopsFixed: z.boolean().optional(),
  marketplaceAccounts: productMarketplaceAccountsSchema.optional(),
  // eBay "Preisvorschlag" / Best Offer. Missing legacy values intentionally
  // resolve to enabled in the sync path so existing products retain the new
  // default until they are explicitly switched off.
  ebayBestOfferEnabled: z.boolean().optional(),
  filter: z.array(z.number()),
  menues: z.array(z.number()).optional(),

  manufacturerDiscountRule: z.string().optional(),

  crossSell: z.array(crossSellSchema).optional(),

  created: z.date(),
  changed: z.date(),
  imported: z.literal(true).optional(),

  deleted: z.literal(true).optional(),
})
export type Product = z.infer<typeof productSchema>
